"use client";

import {
  Building,
  Clock,
  Heart,
  Mail,
  MapPin,
  Navigation as NavigationIcon,
  Phone,
  X,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";

import { useAnalytics } from "@/hooks/useAnalytics";
import type { Aed } from "@/types/aed";

interface AedDetailModalProps {
  aed: Aed | null;
  isOpen: boolean;
  onClose: () => void;
  onDirectionsClick?: () => void;
  onViewDuration?: () => void;
}

export default function AedDetailModal({
  aed,
  isOpen,
  onClose,
  onDirectionsClick,
  onViewDuration,
}: AedDetailModalProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { trackDeaImageView, trackDeaPhoneClick, trackExternalLink, trackButtonClick } =
    useAnalytics();

  // Reset selectedImageIndex when AED changes to avoid index out of bounds
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [aed?.id]);

  // Trigger app download prompt after viewing for 5 seconds
  const aedId = aed?.id;
  useEffect(() => {
    if (!isOpen || !aedId || !onViewDuration) return;
    const timer = setTimeout(onViewDuration, 5000);
    return () => clearTimeout(timer);
  }, [isOpen, aedId, onViewDuration]);

  const handleImageChange = useCallback(
    (index: number) => {
      if (aed) {
        trackDeaImageView(aed.id, index, aed.images?.length || 0);
      }
      setSelectedImageIndex(index);
    },
    [aed, trackDeaImageView]
  );

  const handlePhoneClick = useCallback(
    (phone: string) => {
      if (aed) {
        trackDeaPhoneClick(aed.id, phone);
      }
    },
    [aed, trackDeaPhoneClick]
  );

  const handleDirectionsClick = useCallback(() => {
    if (aed) {
      trackExternalLink(
        `https://www.google.com/maps/search/?api=1&query=${aed.latitude},${aed.longitude}`,
        "Cómo llegar",
        "dea_modal"
      );
      onDirectionsClick?.();
    }
  }, [aed, trackExternalLink, onDirectionsClick]);

  const handleEmailClick = useCallback(
    (_email: string) => {
      if (aed) {
        trackButtonClick("email_responsible", "dea_modal");
      }
    },
    [aed, trackButtonClick]
  );

  if (!isOpen || !aed) return null;

  // Safely calculate the image index within bounds
  const safeImageIndex =
    aed.images && aed.images.length > 0 ? Math.min(selectedImageIndex, aed.images.length - 1) : 0;

  const displayImage =
    aed.images && aed.images.length > 0
      ? aed.images[safeImageIndex]?.processed_url || aed.images[safeImageIndex]?.original_url
      : null;

  return (
    <div
      className="fixed inset-0 z-[1500] flex items-center justify-center p-4"
      style={{
        background:
          "radial-gradient(circle at center, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.9) 100%)",
        backdropFilter: "blur(8px)",
      }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full text-white hover:bg-white/20 transition-all"
          style={{
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(10px)",
          }}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Image Gallery */}
        {displayImage && (
          <div className="relative w-full aspect-square max-h-[500px] mx-auto bg-gray-900 flex items-center justify-center">
            <img
              src={displayImage}
              alt={aed.name}
              className="max-w-full max-h-full object-contain"
            />

            {/* Image Counter */}
            {aed.images && aed.images.length > 1 && (
              <div
                className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full text-white text-sm font-medium"
                style={{
                  background: "rgba(0, 0, 0, 0.6)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {selectedImageIndex + 1} / {aed.images.length}
              </div>
            )}
          </div>
        )}

        {/* Thumbnail Gallery */}
        {aed.images && aed.images.length > 1 && (
          <div className="flex gap-2 p-4 overflow-x-auto bg-gray-50">
            {aed.images.map((image, index) => (
              <button
                key={image.id}
                onClick={() => handleImageChange(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  index === selectedImageIndex
                    ? "border-blue-500 shadow-lg"
                    : "border-gray-300 hover:border-blue-300"
                }`}
              >
                <img
                  src={image.thumbnail_url || image.processed_url || image.original_url}
                  alt={`${aed.name} - ${image.type}`}
                  className="w-full h-full object-contain bg-gray-100"
                />
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{aed.name}</h2>
                <p className="text-sm text-gray-500">{aed.code}</p>
              </div>
              <div
                className="p-3 rounded-full"
                style={{
                  background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
                }}
              >
                <Heart className="w-6 h-6 text-white" />
              </div>
            </div>

            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 text-sm rounded-full font-medium">
              {aed.establishment_type}
            </span>
          </div>

          {/* Details Grid */}
          <div className="grid gap-4 sm:gap-6">
            {/* Location */}
            <div className="flex items-start space-x-3 p-4 rounded-xl bg-gray-50">
              <MapPin className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Ubicación</h3>
                <p className="text-gray-700">
                  {aed.location.street_type} {aed.location.street_name} {aed.location.street_number}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {aed.location.postal_code}
                  {aed.location.district_name && ` - ${aed.location.district_name}`}
                </p>
                {aed.location.access_instructions && (
                  <p className="text-sm text-gray-600 mt-2 italic">
                    📍 {aed.location.access_instructions}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Coordenadas: {aed.latitude.toFixed(6)}, {aed.longitude.toFixed(6)}
                </p>
              </div>
            </div>

            {/* Schedule */}
            {aed.schedule && (
              <div className="flex items-start space-x-3 p-4 rounded-xl bg-purple-50">
                <Clock className="w-6 h-6 text-purple-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Horario</h3>
                  {aed.schedule.has_24h_surveillance ? (
                    <p className="text-gray-700">✅ Vigilancia 24 horas</p>
                  ) : aed.schedule.weekday_opening && aed.schedule.weekday_closing ? (
                    <div className="space-y-0.5">
                      <p className="text-gray-700">
                        L-V: {aed.schedule.weekday_opening} - {aed.schedule.weekday_closing}
                      </p>
                      {aed.schedule.saturday_opening && aed.schedule.saturday_closing && (
                        <p className="text-gray-500 text-sm">
                          Sáb: {aed.schedule.saturday_opening} - {aed.schedule.saturday_closing}
                        </p>
                      )}
                      {aed.schedule.sunday_opening && aed.schedule.sunday_closing && (
                        <p className="text-gray-500 text-sm">
                          Dom: {aed.schedule.sunday_opening} - {aed.schedule.sunday_closing}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-700">Horario no especificado</p>
                  )}
                </div>
              </div>
            )}

            {/* Responsible - Only shown if data is available */}
            {aed.responsible && (
              <div className="flex items-start space-x-3 p-4 rounded-xl bg-green-50">
                <Building className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">Responsable</h3>
                  <p className="text-gray-700 font-medium mb-2">{aed.responsible.name}</p>

                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <a
                        href={`mailto:${aed.responsible.email}`}
                        className="hover:text-blue-600"
                        onClick={() => handleEmailClick(aed.responsible!.email)}
                      >
                        {aed.responsible.email}
                      </a>
                    </div>

                    {aed.responsible.phone && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        <a
                          href={`tel:${aed.responsible.phone}`}
                          className="hover:text-blue-600"
                          onClick={() => handlePhoneClick(aed.responsible!.phone!)}
                        >
                          {aed.responsible.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${aed.latitude},${aed.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all active:scale-95"
              style={{ minHeight: "48px" }}
              onClick={handleDirectionsClick}
            >
              <NavigationIcon className="w-5 h-5" />
              <span>Cómo llegar</span>
            </a>

            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all active:scale-95"
              style={{ minHeight: "48px" }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
